import React, { useState, useEffect } from 'react';
import './HealthMonitor.css';
import { 
  fetchSystemHealthData, 
  fetchSystemMetrics, 
  fetchCompletedOrdersStats, 
  fetchOrdersStatusBreakdown,
  fetchOrdersIssues 
} from './utils/fetch';
import { 
  formatTime, 
  formatTimeInterval, 
  determineStatus, 
  getStatusIcon, 
  getStatusColor, 
  getStatusText, 
  calculateErrorPercentage, 
  formatNumber, 
  groupErrorsByCode, 
  createModuleDetails 
} from './utils/format';

// Функция для перевода статусов заказов на русский язык
const translateOrderStatus = (status) => {
  const statusTranslations = {
    'NEW': 'Новый',
    'ORDER_INSTALLED': 'Установлен',
    'READY_FOR_SHIPMENT': 'Готов к отправке',
    'ORDER_DELIVERED': 'Доставлен',
    'PRODUCTION': 'В производстве',
    'BOOKING': 'Бронирование',
    'REVIEW': 'На проверке',
    'PASSED_TO_MONTAGE': 'Передан в монтаж',
    'REFUSED': 'Отказано',
    'INSTALLING': 'В процессе монтажа',
    'PASSED_TO_ASSEMBLY': 'Передан в сборку',
    'LOOP_MILLING': 'Фрезеровка петель'
  };
  
  return statusTranslations[status] || status;
};

// Функция для перевода типов проблемных заказов на русский язык
const translateIssueType = (issueType) => {
  const issueTranslations = {
    'unpaidCompleted': 'Завершено без оплаты',
    'stuckInProcess': 'Застряли в процессе',
    'deliveryDelays': 'Задержки доставки'
  };
  
  return issueTranslations[issueType] || issueType;
};

const HealthMonitor = () => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [systemData, setSystemData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [ordersStats, setOrdersStats] = useState(null);
  const [ordersBreakdown, setOrdersBreakdown] = useState(null);
  const [ordersIssues, setOrdersIssues] = useState(null);

  useEffect(() => {
    if (typeof document !== "undefined") {
      document.title = "Статус системы";
    }
  }, []);

  // Загрузка данных с бэкенда
  const loadSystemData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Загружаем все данные параллельно для оптимизации
      const [
        healthData,
        metricsData,
        completedStats,
        statusBreakdown,
        issuesData
      ] = await Promise.all([
        fetchSystemHealthData(),
        fetchSystemMetrics('30min'),
        fetchCompletedOrdersStats('30days'),
        fetchOrdersStatusBreakdown(),
        fetchOrdersIssues()
      ]);
      
      // Обрабатываем данные модулей
      const processedModules = {};
      
      if (healthData.modules) {
        Object.entries(healthData.modules).forEach(([key, module]) => {
          const errorLogs = module.errorLogs || [];
          const errors = groupErrorsByCode(errorLogs);
          const successRate = module.successRate || 100;
          const status = determineStatus(successRate);
          
          processedModules[key] = {
            name: module.name || key,
            status: status,
            successRate: successRate,
            timeInterval: formatTimeInterval(module.startTime, module.endTime),
            errors: errors,
            totalRequests: module.totalRequests || 0,
            details: createModuleDetails({
              ...module,
              status: status
            }),
            // Дополнительные данные для специфичных модулей
            processedRequests: module.processedRequests,
            pendingRequests: module.pendingRequests,
            completedRequests: module.completedRequests,
            delayedOrders: module.delayedOrders
          };
        });
      }
      
      setSystemData(processedModules);
      setOrdersStats(completedStats);
      setOrdersBreakdown(statusBreakdown);
      setOrdersIssues(issuesData);
      setLastUpdate(new Date());
    } catch (err) {
      console.error('Ошибка при загрузке данных мониторинга:', err);
      setError('Не удалось загрузить данные мониторинга. Попробуйте обновить страницу.');
    } finally {
      setLoading(false);
    }
  };

  
  // Автоматическое обновление данных каждые 30 секунд
  useEffect(() => {
    loadSystemData();
    
    const dataInterval = setInterval(() => {
      loadSystemData();
    }, 30000); // 30 секунд

    const timeInterval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => {
      clearInterval(dataInterval);
      clearInterval(timeInterval);
    };
  }, []);

  // Подсчет общей статистики
  const calculateOverallStats = () => {
    const modules = Object.values(systemData);
    const totalRequests = modules.reduce((sum, module) => sum + module.totalRequests, 0);
    const okModules = modules.filter(module => module.status === 'ok').length;
    const warningModules = modules.filter(module => module.status === 'warning').length;
    const errorModules = modules.filter(module => module.status === 'error').length;
    
    return {
      totalRequests,
      okModules,
      warningModules,
      errorModules
    };
  };

  const overallStats = calculateOverallStats();

  if (loading && Object.keys(systemData).length === 0) {
    return (
      <div className="health-monitor">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Загрузка данных мониторинга...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="health-monitor">
        <div className="error-container">
          <div className="error-message">
            <span className="error-icon">⚠️</span>
            <p>{error}</p>
            <button onClick={loadSystemData} className="retry-button">
              Попробовать снова
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="health-monitor">
      {/* Заголовок дашборда */}
      <div className="health-monitor-header">
        <div className="header-content">
          <h1 className="dashboard-title">
            <span className="title-icon">📊</span>
            Мониторинг состояния системы
          </h1>
          <div className="header-info">
            <div className="last-update">
              <span className="update-label">Последнее обновление:</span>
              <span className="update-time">{formatTime(lastUpdate)}</span>
            </div>
            <div className="status-legend">
              <div className="legend-item">
                <span className="legend-icon">🟢</span>
                <span>OK</span>
              </div>
              <div className="legend-item">
                <span className="legend-icon">🟡</span>
                <span>Warning</span>
              </div>
              <div className="legend-item">
                <span className="legend-icon">🔴</span>
                <span>Error</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Сетка карточек модулей */}
      <div className="modules-grid">
        {Object.entries(systemData).map(([key, module]) => {
          const errorPercentage = calculateErrorPercentage(module.errors, module.totalRequests);
          const totalErrors = Object.values(module.errors).reduce((sum, count) => sum + count, 0);
          
          return (
            <div
              key={key}
              className={`module-card ${module.status}`}
              style={{
                borderLeft: `4px solid ${getStatusColor(module.status)}`
              }}
            >
              <div className="card-header">
                <div className="module-info">
                  <h3 className="module-name">{module.name}</h3>
                  <div className="status-indicator">
                    <span className="status-icon">{getStatusIcon(module.status)}</span>
                    <span 
                      className="status-text"
                      style={{ color: getStatusColor(module.status) }}
                    >
                      {getStatusText(module.status)}
                    </span>
                  </div>
                </div>
                <div className="time-interval">
                  Промежуток: {module.timeInterval}
                </div>
              </div>
              
              <div className="card-content">
                <div className="success-rate">
                  {module.successRate}% успешных запросов
                </div>
                
                <div className="error-details">
                  {Object.entries(module.errors).map(([errorCode, count]) => (
                    <div key={errorCode} className="error-item">
                      <span className="error-code">Статус: {errorCode}</span>
                      <span className="error-count">
                        - {count} ошибок ({Math.round((count / module.totalRequests) * 100)}%)
                      </span>
                    </div>
                  ))}
                  {Object.keys(module.errors).length === 0 && (
                    <div className="no-errors">Ошибок не обнаружено</div>
                  )}
                </div>

                <div className="module-details">
                  {module.details}
                </div>
              </div>

              <div className="card-footer">
                <div className="module-stats">
                  <div className="stat-item">
                    <span className="stat-label">Всего запросов:</span>
                    <span className="stat-value">{formatNumber(module.totalRequests)}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Ошибок:</span>
                    <span className="stat-value" style={{ color: getStatusColor(module.status) }}>
                      {totalErrors} ({errorPercentage}%)
                    </span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Общая статистика */}
      <div className="overall-stats">
        <div className="stats-card">
          <h3>Общая статистика системы</h3>
          <div className="stats-grid">
            <div className="stat-box ok">
              <div className="stat-number">{overallStats.okModules}</div>
              <div className="stat-label">Модулей OK</div>
            </div>
            <div className="stat-box warning">
              <div className="stat-number">{overallStats.warningModules}</div>
              <div className="stat-label">Предупреждений</div>
            </div>
            <div className="stat-box error">
              <div className="stat-number">{overallStats.errorModules}</div>
              <div className="stat-label">Критических ошибок</div>
            </div>
            <div className="stat-box info">
              <div className="stat-number">{formatNumber(overallStats.totalRequests)}</div>
              <div className="stat-label">Всего запросов</div>
            </div>
          </div>
        </div>
      </div>

      {/* Детальная статистика заказов */}
      {ordersStats && ordersBreakdown && (
        <div className="orders-details">
          <div className="orders-stats-card">
            <h3>Детальная статистика заказов</h3>
            <div className="orders-stats-grid">
              <div className="order-stat-box">
                <div className="order-stat-number">{formatNumber(ordersStats.statistics?.totalOrders || 0)}</div>
                <div className="order-stat-label">Всего заказов</div>
              </div>
              <div className="order-stat-box">
                <div className="order-stat-number">{formatNumber(ordersStats.statistics?.completedOrders || 0)}</div>
                <div className="order-stat-label">Завершено</div>
              </div>
              <div className="order-stat-box">
                <div className="order-stat-number">{ordersStats.statistics?.completionRate || 0}%</div>
                <div className="order-stat-label">Процент завершения</div>
              </div>
              <div className="order-stat-box">
                <div className="order-stat-number">{ordersStats.statistics?.averageCompletionTime?.days || 0}</div>
                <div className="order-stat-label">Среднее время (дни)</div>
              </div>
            </div>
          </div>

          {/* Разбивка по статусам */}
          <div className="status-breakdown-card">
            <h4>Разбивка заказов по статусам</h4>
            <div className="status-breakdown-grid">
              {ordersBreakdown.statusBreakdown && Object.entries(ordersBreakdown.statusBreakdown).map(([status, data]) => (
                <div key={status} className="status-item">
                  <div className="status-name">{translateOrderStatus(status)}</div>
                  <div className="status-count">{formatNumber(data.count)}</div>
                  <div className="status-percentage">{data.percentage}%</div>
                </div>
              ))}
            </div>
          </div>

          {/* Проблемные заказы */}
          {ordersIssues && Object.keys(ordersIssues.issues || {}).length > 0 && (
            <div className="issues-card">
              <h4>Проблемные заказы</h4>
              <div className="issues-grid">
                {Object.entries(ordersIssues.issues).map(([issueType, issueData]) => (
                  <div key={issueType} className="issue-item">
                    <div className="issue-type">{translateIssueType(issueType)}</div>
                    <div className="issue-count">{formatNumber(issueData.count)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default HealthMonitor;
